import { FC } from 'react'
import { FlexboxGrid, Panel } from 'rsuite'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Link from 'next/link'

import { Supplier } from '../../utils/types/Product'
import { getMediumPreview, imageLoader } from '../../utils/images'
import Carousel from '../Layouts/Carousel'
import useUrls from '../../services/useUrls'
import { getSupplierDeliveryDetails } from '../../lib/service'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import { MEDIA_TYPE_SUPPLIER_IMAGE } from '../hooks/useSupplierProfileData'
import useIsOnMobile from '../../services/useIsOnMobile'
import useTypesenseSuppliers from '../../services/useTypesenseSuppliers'

import styles from '../../styles/Carousels/SupplierVibeIconCarousel.module.less'

interface SupplierVibeIconCarouselProps {
  carouselTitle?: string
  className?: string
}

const SupplierVibeIconCarousel: FC<SupplierVibeIconCarouselProps> = ({
  carouselTitle,
  className = '',
}) => {
  const { urlT } = useUrls()
  const { locale, pathname } = useRouter()
  const isOnMobile = useIsOnMobile()
  const { country } = getCountryAndLocaleStrings(locale)

  // Use Typesense suppliers hook with country filtering
  const { data: suppliers, isLoading } = useTypesenseSuppliers({
    limit: 50,
    countries: [country],
    sortBy: 'pos:asc',
  })

  // Get vibe icon for each supplier
  const getSupplierVibeIcon = (supplier: Supplier) => {
    const vibeIcon = supplier.media?.find((media) => media['media.type'] === MEDIA_TYPE_SUPPLIER_IMAGE)
    return vibeIcon ? getMediumPreview(vibeIcon) : null
  }

  // Show carousel on home page, SPP, category, suppliers and supplier show pages
  if (isLoading || !suppliers.length || !(pathname === '/' || pathname?.startsWith('/product') || pathname?.startsWith('/supplier'))) {
    return null
  }

  return (
    <div className={`${styles['vibe-icon-carousel-wrapper']} ${className}`}>
      {carouselTitle && <h2 className="h4">{carouselTitle}</h2>}
      <FlexboxGrid className={styles['supplier-carousel']}>
        <Carousel
          hideArrows={suppliers.length < 6}
          gap={isOnMobile ? 15 : 30}
        >
          {suppliers.map((supplier) => {
            const supplierName = supplier['supplier.label']
            const supplierCode = supplier['supplier.code']
            const vibeIconUrl = getSupplierVibeIcon(supplier)
            const imageAlt = `${supplierName} vibe icon`

            if (!vibeIconUrl) return null

            return (
              <Link
                key={supplierCode}
                href={urlT(`/supplier/${supplierCode}`)}
                className={styles['carousel-card']}
              >
                <Panel
                  className={styles['vibe-icon-container']}
                  bodyFill
                >
                  <Image
                    loader={imageLoader}
                    src={vibeIconUrl}
                    alt={imageAlt}
                    layout="fill"
                    objectFit="contain"
                  />
                </Panel>
              </Link>
            )
          })}
        </Carousel>
      </FlexboxGrid>
    </div>
  )
}

export default SupplierVibeIconCarousel
